import { FC, memo, MouseEventHandler, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  styled,
  Tooltip,
} from '@mui/material';

import { HasPermission } from '@components/auth';
import { Avatar, FlagChip, Icon, IconButton, Menu } from '@components/base';
import { useProviderNames } from '@hooks/useProviderNames';
import { DetailedPlaybackOption, DetailedTitle } from '@models/Title';
import useMasterIdManager from '@modules/masterIdManager/useMasterIdManager';
import { AvailabilityMap, getAvailability } from '@utils/availabilityUtils';
import { formatDate, formatRelativeTime } from '@utils/dateUtils';

import Section from './Section';

const StyledChipCatchUp = styled(Chip)`
  padding: ${props => props.theme.spacing()};
  background: ${props => props.theme.palette.warning.main};
  color: ${props => props.theme.palette.warning.contrastText};

  & > span {
    display: none;
  }
`;

const NestedList = styled(List)`
  margin-bottom: ${({ theme }) => theme.spacing(0.75)};
`;

const NestedListItemText = styled(ListItemText)`
  margin: 0;
`;

const AvailabilityChip = styled(Chip)`
  width: 100%;
`;

type PropsWithTitle = {
  title: DetailedTitle;
};

type PropsWithPlaybackOption = { playbackOption: DetailedPlaybackOption };

const PlaybackOptionCatchupChip: FC<PropsWithPlaybackOption> = ({ playbackOption: { data } }) => {
  if (!data) return null;

  const supports = [];

  if (data.supportsCatchup) supports.push('catchup');
  if (data.supportsCloudRecording) supports.push('cloud recording');
  if (data.supportsLocalRecording) supports.push('local recording');

  if (supports.length === 0) return null;

  const tooltip = 'Supports ' + supports.join(', ');

  return (
    <Tooltip title={tooltip}>
      <StyledChipCatchUp
        avatar={
          <>
            {data.supportsCatchup && <Icon fontSize='small' name='replay' />}
            {data.supportsCloudRecording && <Icon fontSize='small' name='cloud' />}
            {data.supportsLocalRecording && <Icon fontSize='small' name='tv' />}
          </>
        }
        size='small'
      />
    </Tooltip>
  );
};

type PlaybackOptionMenuProps = PropsWithPlaybackOption & { title: DetailedTitle };

const PlaybackOptionsMenu: FC<PlaybackOptionMenuProps> = ({ playbackOption, title }) => {
  const { openMovePlaybackOptionView, handleSplitIntoNewTitle } = useMasterIdManager();

  const handleMoveToAnotherTitle: MouseEventHandler<HTMLLIElement> = e => {
    e.stopPropagation();
    openMovePlaybackOptionView(title, playbackOption);
  };

  const onSplitIntoNewTitle: MouseEventHandler<HTMLLIElement> = e => {
    e.stopPropagation();
    handleSplitIntoNewTitle(title, playbackOption);
  };

  if (title.playbackOptions.length < 2) return null;

  return (
    <Menu>
      <Menu.IconButton onClick={e => e.stopPropagation()}>
        <Icon fontSize='small' name='moreVert' />
      </Menu.IconButton>

      <Menu.Content>
        <Menu.Item onClick={handleMoveToAnotherTitle}>Move to another title</Menu.Item>
        <Menu.Item onClick={onSplitIntoNewTitle}>Split into new title</Menu.Item>
      </Menu.Content>
    </Menu>
  );
};

type PlaybackOptionListItemProps = {
  index: number;
  playbackOption: DetailedPlaybackOption;
  title: DetailedTitle;
};

const PlaybackOptionListItem: FC<PlaybackOptionListItemProps> = ({
  index,
  playbackOption,
  playbackOption: {
    availableFrom,
    availableTo,
    data,
    endTime,
    id,
    isDefault,
    location,
    platformTitleId,
    providerIdentifier,
    providerTitleId,
    serviceType,
    sourceId,
    startTime,
  },
  title,
  title: { playbackOptions },
}) => {
  const { t } = useTranslation();
  const { getProviderName, getProviderImage } = useProviderNames();

  const [expanded, setExpanded] = useState(false);

  const isExpandable = Boolean(
    availableFrom ||
      availableTo ||
      endTime ||
      id ||
      location ||
      platformTitleId ||
      providerTitleId ||
      serviceType ||
      sourceId ||
      startTime
  );

  const imageUrl = getProviderImage(providerIdentifier);

  const availability = getAvailability(availableFrom, availableTo);
  const availabilityDefs = AvailabilityMap[availability];

  const availableFromStr = availableFrom ? formatDate(availableFrom, 'full') : undefined;
  const availableToStr = availableTo ? formatDate(availableTo, 'full') : undefined;

  const listItemContent = (
    <>
      <ListItemAvatar>
        <Avatar size='large' src={imageUrl} sx={{ borderRadius: 1, my: 0.5 }}>
          {!imageUrl && <Icon fontSize='small' name={data ? 'liveTv' : 'onDemandVideo'} />}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={getProviderName(providerIdentifier)}
        secondary={startTime && formatRelativeTime(startTime)}
      />

      <Stack alignItems='center' component={ListItemSecondaryAction} direction='row' spacing={0.5}>
        {location && (
          <FlagChip
            countryCode={location}
            countryString={t('country', { context: location.toLowerCase() })}
            TooltipProps={{ placement: 'left' }}
          />
        )}

        <Box width={76}>
          <AvailabilityChip
            color={availabilityDefs.severity}
            label={t('available', { context: availability })}
            size='small'
            title={t('available.description', {
              context: availability,
              availableFrom: availableFromStr,
              availableTo: availableToStr,
            })}
            variant='outlined'
          />
        </Box>

        {isDefault && <Chip label='default' size='small' />}

        <PlaybackOptionCatchupChip playbackOption={playbackOption} />

        <HasPermission>
          <PlaybackOptionsMenu playbackOption={playbackOption} title={title} />
        </HasPermission>

        {isExpandable && <IconButton icon={expanded ? 'expandLess' : 'expandMore'} />}
      </Stack>
    </>
  );

  return isExpandable ? (
    <>
      <ListItemButton onClick={() => setExpanded(!expanded)}>{listItemContent}</ListItemButton>

      <Collapse in={expanded}>
        <NestedList dense>
          {sourceId && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText primary={t('sourceId')} secondary={sourceId} />
            </ListItem>
          )}

          {serviceType && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText primary={t('serviceType')} secondary={serviceType} />
            </ListItem>
          )}

          {id && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText primary={t('id')} secondary={id} />
            </ListItem>
          )}

          {platformTitleId && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText primary={t('platformTitleId')} secondary={platformTitleId} />
            </ListItem>
          )}

          {providerTitleId && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText primary={t('providerTitleId')} secondary={providerTitleId} />
            </ListItem>
          )}

          {startTime && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText
                primary={t('startTime')}
                secondary={formatDate(startTime, 'full')}
                title={startTime}
              />
            </ListItem>
          )}

          {endTime && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText
                primary={t('endTime')}
                secondary={formatDate(endTime, 'full')}
                title={endTime}
              />
            </ListItem>
          )}

          {availableFrom && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText
                primary={t('availableFrom')}
                secondary={availableFromStr}
                title={availableFrom}
              />
            </ListItem>
          )}

          {availableTo && (
            <ListItem dense>
              <ListItemAvatar />
              <NestedListItemText
                primary={t('availableTo')}
                secondary={availableToStr}
                title={availableTo}
              />
            </ListItem>
          )}
        </NestedList>
      </Collapse>

      {index < playbackOptions.length - 1 && <Divider />}
    </>
  ) : (
    <ListItem>{listItemContent}</ListItem>
  );
};

const PlaybackOptions: FC<PropsWithTitle> = ({ title, title: { playbackOptions } }) => {
  const { t } = useTranslation();
  const hasContent = playbackOptions?.length > 0;
  const [visibleCount, setVisibleCount] = useState(20);

  const cleanList = useMemo(
    () => Array.from(new Map(playbackOptions.map(item => [item.id, item])).values()),
    [playbackOptions]
  );

  const handleShowMore = () => setVisibleCount(c => Math.min(c + 20, playbackOptions.length));

  return (
    <Section
      category='playbackOptions'
      empty={!hasContent}
      heading={t('titleDetails.playbackOptions_all')}
    >
      {cleanList?.slice(0, visibleCount).map((playbackOption, index) => (
        <PlaybackOptionListItem
          index={index}
          key={playbackOption.id}
          playbackOption={playbackOption}
          title={title}
        />
      ))}

      {visibleCount < cleanList.length && (
        <Box display='flex' justifyContent='center' my={2}>
          <Chip
            label={t('showMore', { count: cleanList.length - visibleCount })}
            onClick={handleShowMore}
          />
        </Box>
      )}
    </Section>
  );
};

export default memo(PlaybackOptions);
